import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UseVoiceInputReturn {
  isSupported: boolean;
  isListening: boolean;
  isTranscribing: boolean;
  startListening: () => void;
  stopListening: () => void;
}

/** Check if MediaRecorder + getUserMedia are available */
const checkSupport = (): boolean => {
  if (typeof window === "undefined") return false;
  return !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
};

const getAudioFilename = (mimeType: string): string => {
  if (mimeType.includes("mp4")) return "recording.mp4";
  if (mimeType.includes("mpeg")) return "recording.mp3";
  if (mimeType.includes("ogg")) return "recording.ogg";
  if (mimeType.includes("wav")) return "recording.wav";
  return "recording.webm";
};

export const useVoiceInput = (
  onTranscript: (text: string) => void,
): UseVoiceInputReturn => {
  const isSupported = checkSupport();

  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const onTranscriptRef = useRef(onTranscript);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mediaRecorderRef.current?.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const transcribe = useCallback(async (audioBlob: Blob) => {
    setIsTranscribing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Please sign in to use voice input.");
        return;
      }

      const formData = new FormData();
      formData.append("audio", audioBlob, getAudioFilename(audioBlob.type));

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: formData,
        },
      );

      if (!res.ok) {
        const errorBody = await res.text().catch(() => "");
        console.error("Voice transcription response:", res.status, errorBody);
        throw new Error(`Transcription failed (${res.status})`);
      }

      const { text } = await res.json();
      if (text?.trim()) {
        onTranscriptRef.current(text.trim());
      } else {
        toast.info("Didn't catch that — try again?");
      }
    } catch (err) {
      console.error("Voice transcription error:", err);
      toast.error("Voice transcription failed — try again.");
    } finally {
      setIsTranscribing(false);
    }
  }, []);

  const startListening = useCallback(async () => {
    if (isListening || isTranscribing) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      // Prefer webm/opus, then let the browser choose if neither preferred type is supported.
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        // Release mic
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        const blob = new Blob(chunksRef.current, { type: mimeType || recorder.mimeType });
        chunksRef.current = [];

        if (blob.size > 0) {
          transcribe(blob);
        }
      };

      recorder.onerror = () => {
        setIsListening(false);
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        toast.error("Recording failed — try again.");
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsListening(true);
    } catch (err) {
      console.error("Mic access error:", err);
      toast.error("Couldn't access microphone. Check your browser permissions.");
    }
  }, [isListening, isTranscribing, transcribe]);

  const stopListening = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    mediaRecorderRef.current = null;
    setIsListening(false);
  }, []);

  return {
    isSupported,
    isListening,
    isTranscribing,
    startListening,
    stopListening,
  };
};
